/**
 * Company Service
 *
 * Provides read/write access to the current company's invoicing details
 * (name, address, logo, VAT number, registration number, bank details, contact info).
 *
 * All functions call the backend API endpoints via /api/companies/{companyId}/details.
 * Returns default values when no companyId is available (e.g. demo mode).
 */

import type { Company, BankDetails } from '@/types/company';
import type { Address } from '@/types/index';
import { getApiBaseUrl } from '@/lib/data-service';

// ============================================================================
// TYPES
// ============================================================================

/**
 * The subset of Company fields that are managed by the Company Details settings page.
 * These are the fields that appear on invoices and can be edited by the user.
 */
export interface CompanyDetails {
  name: string;
  companyRegistrationNumber?: string;
  vatNumber?: string;
  phone?: string;
  email?: string;
  website?: string;
  logoUrl?: string;
  registeredAddress?: Address;
  bankDetails?: BankDetails;
}

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_COMPANY_DETAILS: CompanyDetails = {
  name: '',
  companyRegistrationNumber: '',
  vatNumber: '',
  phone: '',
  email: '',
  website: '',
  logoUrl: undefined,
  registeredAddress: {
    line1: '',
    line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
  },
  bankDetails: {
    bankName: '',
    accountName: '',
    sortCode: '',
    accountNumber: '',
    iban: '',
    swiftBic: '',
  },
};

// ============================================================================
// HELPERS — map flat DB columns to nested frontend shape and back
// ============================================================================

function mapApiResponseToDetails(data: Record<string, unknown>): CompanyDetails {
  return {
    name: (data.name as string) ?? '',
    companyRegistrationNumber: data.companyRegistrationNumber as string | undefined,
    vatNumber: data.vatNumber as string | undefined,
    phone: data.phone as string | undefined,
    email: data.email as string | undefined,
    website: data.website as string | undefined,
    logoUrl: data.logoUrl as string | undefined,
    registeredAddress: {
      line1: (data.registeredAddressLine1 as string) ?? '',
      line2: (data.registeredAddressLine2 as string) ?? '',
      city: (data.registeredAddressCity as string) ?? '',
      county: (data.registeredAddressCounty as string) ?? '',
      postcode: (data.registeredAddressPostcode as string) ?? '',
      country: (data.registeredAddressCountry as string) ?? 'United Kingdom',
    },
    bankDetails: {
      bankName: (data.bankName as string) ?? '',
      accountName: (data.bankAccountName as string) ?? '',
      sortCode: (data.bankSortCode as string) ?? '',
      accountNumber: (data.bankAccountNumber as string) ?? '',
      iban: (data.bankIban as string) ?? '',
      swiftBic: (data.bankSwiftBic as string) ?? '',
    },
  };
}

function mapDetailsToApiPayload(details: CompanyDetails): Record<string, unknown> {
  return {
    companyRegistrationNumber: details.companyRegistrationNumber ?? null,
    vatNumber: details.vatNumber ?? null,
    phone: details.phone ?? null,
    email: details.email ?? null,
    website: details.website ?? null,
    logoUrl: details.logoUrl ?? null,
    registeredAddressLine1: details.registeredAddress?.line1 ?? null,
    registeredAddressLine2: details.registeredAddress?.line2 ?? null,
    registeredAddressCity: details.registeredAddress?.city ?? null,
    registeredAddressCounty: details.registeredAddress?.county ?? null,
    registeredAddressPostcode: details.registeredAddress?.postcode ?? null,
    registeredAddressCountry: details.registeredAddress?.country ?? null,
    bankName: details.bankDetails?.bankName ?? null,
    bankAccountName: details.bankDetails?.accountName ?? null,
    bankSortCode: details.bankDetails?.sortCode ?? null,
    bankAccountNumber: details.bankDetails?.accountNumber ?? null,
    bankIban: details.bankDetails?.iban ?? null,
    bankSwiftBic: details.bankDetails?.swiftBic ?? null,
  };
}


// ============================================================================
// COMPANY ID RESOLUTION
// ============================================================================

let _companyId: number | null = null;

/**
 * Set the current company ID so that subsequent service calls target the
 * correct backend resource. Called from CompanyContext or app bootstrap.
 */
export function setCurrentCompanyId(id: number | null): void {
  _companyId = id;
}

function getBaseUrl(): string | null {
  return _companyId ? `${getApiBaseUrl()}/companies/${_companyId}` : null;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Returns the current company's invoicing details.
 *
 * API: GET /api/companies/{id}/details
 */
export async function getCompanyDetails(): Promise<CompanyDetails> {
  const base = getBaseUrl();
  if (!base) return { ...DEFAULT_COMPANY_DETAILS };

  const response = await fetch(`${base}/details`, { credentials: 'include' });
  if (!response.ok) throw new Error(`Failed to fetch company details: ${response.statusText}`);
  const data = await response.json();
  return mapApiResponseToDetails(data);
}

// ============================================================================
// WRITE
// ============================================================================

/**
 * Saves updated company details.
 *
 * API: PUT /api/companies/{id}/details
 */
export async function updateCompanyDetails(
  updates: Partial<CompanyDetails>,
): Promise<CompanyDetails> {
  const current = await getCompanyDetails();
  const merged: CompanyDetails = {
    ...current,
    ...updates,
    registeredAddress:
      updates.registeredAddress !== undefined
        ? { ...current.registeredAddress, ...updates.registeredAddress }
        : current.registeredAddress,
    bankDetails:
      updates.bankDetails !== undefined
        ? ({ ...current.bankDetails, ...updates.bankDetails } as BankDetails)
        : current.bankDetails,
  };

  const base = getBaseUrl();
  if (!base) {
    throw new Error('Cannot update company details: no company ID set');
  }

  const response = await fetch(`${base}/details`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(mapDetailsToApiPayload(merged)),
  });
  if (!response.ok) {
    throw new Error(`Failed to update company details: ${response.statusText}`);
  }
  return merged;
}

/**
 * Uploads a company logo.
 *
 * API: POST /api/companies/{id}/logo
 */
export async function uploadCompanyLogo(base64DataUrl: string): Promise<string> {
  const base = getBaseUrl();
  if (!base) {
    throw new Error('Cannot upload logo: no company ID set');
  }

  const response = await fetch(`${base}/logo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ logoUrl: base64DataUrl }),
  });
  if (!response.ok) {
    throw new Error(`Failed to upload company logo: ${response.statusText}`);
  }
  const data = await response.json();
  return data.logoUrl;
}

/**
 * Removes the company logo.
 *
 * API: DELETE /api/companies/{id}/logo
 */
export async function removeCompanyLogo(): Promise<void> {
  const base = getBaseUrl();
  if (!base) {
    throw new Error('Cannot remove logo: no company ID set');
  }

  const response = await fetch(`${base}/logo`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to remove company logo: ${response.statusText}`);
  }
}

/**
 * Maps a CompanyDetails object to the fields expected by the Company type
 * used throughout the application. Useful when merging saved details back
 * into CompanyContext.
 */
export function mapToCompanyFields(details: CompanyDetails): Partial<Company> {
  return {
    name: details.name,
    companyRegistrationNumber: details.companyRegistrationNumber,
    vatNumber: details.vatNumber,
    phone: details.phone,
    email: details.email,
    website: details.website,
    logoUrl: details.logoUrl,
    registeredAddress: details.registeredAddress,
    bankDetails: details.bankDetails,
  };
}
